import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { ebayApiService, BoxSetData } from '../../services/ebayApi'

interface BoxSetsState {
  boxSets: BoxSetData[]
  selectedBox: BoxSetData | null
  loading: boolean
  error: string | null
  lastUpdated: string | null
  filters: {
    game?: string
    priceRange?: { min: number; max: number }
    condition?: string
    sortBy?: 'name' | 'price' | 'totalSold' | 'priceChange'
    sortOrder?: 'asc' | 'desc'
  }
}

const initialState: BoxSetsState = {
  boxSets: [],
  selectedBox: null,
  loading: false,
  error: null,
  lastUpdated: null,
  filters: {
    sortBy: 'totalSold',
    sortOrder: 'desc'
  }
}

// Async thunks for API calls
export const fetchAllBoxSets = createAsyncThunk(
  'boxSets/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const boxSets = await ebayApiService.getAllBoxSets()
      return boxSets
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch box sets')
    }
  }
)

export const fetchBoxSetDetails = createAsyncThunk(
  'boxSets/fetchDetails',
  async (id: number, { rejectWithValue }) => {
    try {
      const boxSet = await ebayApiService.getBoxSetDetails(id)
      if (!boxSet) {
        throw new Error('Box set not found')
      }
      return boxSet
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch box set details')
    }
  }
)

export const refreshMarketData = createAsyncThunk(
  'boxSets/refreshMarketData',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { boxSets: BoxSetsState }
      const currentBoxSets = state.boxSets.boxSets
      
      // Refresh market insights for all box sets
      const refreshedBoxSets = await Promise.all(
        currentBoxSets.map(async (boxSet) => {
          const marketInsights = await ebayApiService.getMarketInsights(boxSet.name, 7)
          return {
            ...boxSet,
            avgSoldPrice: marketInsights.avgSoldPrice,
            totalSold: marketInsights.totalSold,
            priceChange24h: marketInsights.priceChange,
            marketInsights
          }
        })
      )
      
      return refreshedBoxSets
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to refresh market data')
    }
  }
)

const boxSetsSlice = createSlice({
  name: 'boxSets',
  initialState,
  reducers: {
    setSelectedBox: (state, action: PayloadAction<BoxSetData | null>) => {
      state.selectedBox = action.payload
    },
    setFilters: (state, action: PayloadAction<Partial<BoxSetsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    sortBoxSets: (state, action: PayloadAction<{ sortBy: BoxSetsState['filters']['sortBy']; sortOrder: BoxSetsState['filters']['sortOrder'] }>) => {
      const { sortBy, sortOrder } = action.payload
      state.filters.sortBy = sortBy
      state.filters.sortOrder = sortOrder
      
      state.boxSets.sort((a: BoxSetData, b: BoxSetData) => {
        let aValue: number | string = 0
        let bValue: number | string = 0
        
        switch (sortBy) {
          case 'name':
            aValue = a.name.toLowerCase()
            bValue = b.name.toLowerCase()
            break
          case 'price':
            aValue = a.currentPrice
            bValue = b.currentPrice
            break
          case 'totalSold':
            aValue = a.totalSold
            bValue = b.totalSold
            break
          case 'priceChange':
            aValue = a.priceChange24h
            bValue = b.priceChange24h
            break
          default:
            return 0
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
        } else {
          return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number)
        }
      })
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all box sets
      .addCase(fetchAllBoxSets.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllBoxSets.fulfilled, (state, action) => {
        state.loading = false
        state.boxSets = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      .addCase(fetchAllBoxSets.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // Fetch box set details
      .addCase(fetchBoxSetDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBoxSetDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedBox = action.payload
        
        // Update the box in the main list if it exists
        const index = state.boxSets.findIndex((box: BoxSetData) => box.id === action.payload.id)
        if (index !== -1) {
          state.boxSets[index] = action.payload
        }
      })
      .addCase(fetchBoxSetDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // Refresh market data
      .addCase(refreshMarketData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshMarketData.fulfilled, (state, action) => {
        state.loading = false
        state.boxSets = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      .addCase(refreshMarketData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const { setSelectedBox, setFilters, clearError, sortBoxSets } = boxSetsSlice.actions

// Selectors
export const selectBoxSets = (state: { boxSets: BoxSetsState }) => state.boxSets.boxSets
export const selectSelectedBox = (state: { boxSets: BoxSetsState }) => state.boxSets.selectedBox
export const selectBoxSetsLoading = (state: { boxSets: BoxSetsState }) => state.boxSets.loading
export const selectBoxSetsError = (state: { boxSets: BoxSetsState }) => state.boxSets.error
export const selectBoxSetsFilters = (state: { boxSets: BoxSetsState }) => state.boxSets.filters
export const selectLastUpdated = (state: { boxSets: BoxSetsState }) => state.boxSets.lastUpdated

// Filtered and sorted selectors
export const selectFilteredBoxSets = (state: { boxSets: BoxSetsState }) => {
  const { boxSets, filters } = state.boxSets
  
  return boxSets.filter(box => {
    // Game filter
    if (filters.game && box.game.toLowerCase() !== filters.game.toLowerCase()) {
      return false
    }
    
    // Price range filter
    if (filters.priceRange) {
      const { min, max } = filters.priceRange
      if (box.currentPrice < min || box.currentPrice > max) {
        return false
      }
    }
    
    // Condition filter
    if (filters.condition && box.condition.toLowerCase() !== filters.condition.toLowerCase()) {
      return false
    }
    
    return true
  })
}

export const selectTopPerformingBoxSets = (state: { boxSets: BoxSetsState }, limit: number = 5) => {
  const boxSets = selectFilteredBoxSets(state)
  return boxSets
    .slice()
    .sort((a, b) => b.totalSold - a.totalSold)
    .slice(0, limit)
}

export const selectTrendingBoxSets = (state: { boxSets: BoxSetsState }, limit: number = 5) => {
  const boxSets = selectFilteredBoxSets(state)
  return boxSets
    .slice()
    .sort((a, b) => b.priceChange24h - a.priceChange24h)
    .slice(0, limit)
}

export default boxSetsSlice.reducer
